import {useContext} from "react";
import PropTypes from "prop-types";
import CreditCardIcon from "@heroicons/react/outline/CreditCardIcon";
import getSymbolFromCurrency from "currency-symbol-map";

import FormContext from "../form-context";

export {getHoldCaptureAgreement} from "../utils/payment-consent";

const MODE_CONTENT = {
    "card-capture": {
        eyebrow: "Save a card",
        defaultSummary: "The card will be saved for future authorised payments and card holds."
    },
    "security-hold": {
        eyebrow: "Card hold"
    },
    "take-payment": {
        eyebrow: "Card payment"
    }
};

const formatAmount = (amount, currency) => {
    if (amount === undefined || amount === null || amount === "") return null;
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return null;

    const value = numericAmount / 100;
    const fractionDigits = value !== Math.floor(value) ? 2 : 0;
    const formattedNumber = value.toLocaleString("en", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });
    const symbol = getSymbolFromCurrency(currency) || "";
    return `${symbol}${formattedNumber} ${currency}`.trim();
};

const getConfiguredAmount = ({customerEntersAmount, fixedAmount, minimumAmount, maximumAmount, currency}) => {
    if (!customerEntersAmount) return formatAmount(fixedAmount, currency);

    const minimum = formatAmount(minimumAmount, currency);
    const maximum = formatAmount(maximumAmount, currency);
    if (minimum && maximum) return `${minimum}–${maximum}`;
    if (minimum) return `${minimum} or more`;
    if (maximum) return `up to ${maximum}`;
    return `an amount in ${currency}`;
};

export const getPaymentPreviewContent = (props) => {
    const mode = MODE_CONTENT[props.mode] ? props.mode : "card-capture";
    const content = MODE_CONTENT[mode];
    const currency = props.currency || "AUD";
    const amount = getConfiguredAmount({...props, currency});

    if (mode === "take-payment") {
        return {
            ...content,
            summary: props.customerEntersAmount
                ? `The customer chooses ${amount} to pay.`
                : amount
                    ? `${amount} will be charged when the form is submitted.`
                    : "The configured amount will be charged when the form is submitted."
        };
    }

    if (mode === "security-hold") {
        const duration = Number(props.holdDurationHours);
        const durationText = Number.isFinite(duration) && duration > 0
            ? ` for up to ${duration} ${duration === 1 ? "hour" : "hours"}`
            : " temporarily";
        const outcomeText = props.allowHoldCapture
            ? " and may later be taken as payment under the agreed terms."
            : ". It will not be taken as payment.";
        return {
            ...content,
            summary: props.customerEntersAmount
                ? `The customer chooses ${amount}. That amount will be held${durationText}${outcomeText}`
                : amount
                    ? `${amount} will be held${durationText}${outcomeText}`
                    : `The configured amount will be held${durationText}${outcomeText}`
        };
    }

    return {...content, summary: content.defaultSummary};
};

export const getPaymentCustomerAccessSummary = props => {
    const accountMode = props.mode === "take-payment"
        ? props.customerAccountMode || (props.allowGuestPayment ? "none" : "required")
        : "required";

    if (accountMode === "none") return "Guest Checkout";
    if (accountMode === "optional") {
        return props.allowInlineRegistration === false
            ? "Guest Checkout Or Sign In"
            : "Guest + Optional Account";
    }

    return props.allowInlineRegistration === false
        ? "Sign In Required"
        : "Account Required";
};

/**
 * Builder-safe representation of a payment field. Live website forms replace this with the Stripe-hosted control.
 */
export default function Payment(props) {
    const formContext = useContext(FormContext);
    const content = getPaymentPreviewContent(props);
    const unavailableReason = formContext?.paymentFieldsDisabledReason;
    const customerAccessSummary = getPaymentCustomerAccessSummary(props);
    const summaryItems = [
        customerAccessSummary,
        "Agreement Required",
        props.consentText?.trim() ? "Extra Terms Added" : null
    ].filter(Boolean);

    return (
        <div
            className="rounded-lg border border-panels-300 bg-white px-4 py-3"
            data-testid="payment-field-preview"
            role="group"
            aria-label="Payment field preview"
        >
            <div className="flex items-start gap-3">
                <CreditCardIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" aria-hidden="true"/>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{content.eyebrow}</div>
                    <p className="mb-0 mt-1 text-xs leading-5 text-gray-600">{content.summary}</p>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {summaryItems.map(item => (
                    <span
                        key={item}
                        className="rounded-full border border-panels-200 bg-panels-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                    >
                        {item}
                    </span>
                ))}
            </div>
            {unavailableReason ? (
                <p className="mb-0 mt-3 border-t border-panels-200 pt-2 text-xs font-medium text-red-600">
                    {unavailableReason}
                </p>
            ) : null}
        </div>
    );
}

Payment.propTypes = {
    mode: PropTypes.oneOf(["card-capture", "security-hold", "take-payment"]),
    customerEntersAmount: PropTypes.bool,
    fixedAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minimumAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maximumAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    holdDurationHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    allowHoldCapture: PropTypes.bool,
    holdCaptureTerms: PropTypes.string,
    consentText: PropTypes.string,
    customerAccountMode: PropTypes.oneOf(["none", "optional", "required"]),
    allowGuestPayment: PropTypes.bool,
    allowInlineRegistration: PropTypes.bool
};

Payment.defaultProps = {
    mode: "card-capture",
    customerEntersAmount: false,
    currency: "AUD",
    allowHoldCapture: false
};
